/**
 * ZIP Download Service
 *
 * Modular service for creating ZIP archives from multiple files.
 * Follows SOLID principles:
 * - Single Responsibility: Only handles ZIP creation
 * - Open/Closed: Extensible via strategies
 * - Dependency Inversion: Depends on abstractions (types)
 */

import JSZip from 'jszip';
import type {
  ZipArchiveConfig,
  ZipFileEntry,
  ZipProgress,
  ZipProgressCallback,
  ZipResult,
} from './types';
import { URLService } from '../url.service';

import { handleError } from '../../utils/errorHandler';

/**
 * Service for creating and downloading ZIP archives.
 */
export class ZipDownloadService {
  private zip: JSZip;

  constructor() {
    this.zip = new JSZip();
  }

  /**
   * Create a ZIP archive from the given configuration.
   *
   * @param config - Archive configuration with files to include
   * @param onProgress - Optional callback for progress updates
   * @returns Promise with the result containing blob URL or error
   */
  async createArchive(
    config: ZipArchiveConfig,
    onProgress?: ZipProgressCallback
  ): Promise<ZipResult> {
    const { files, includeManifest, manifestData } = config;
    const failedFiles: string[] = [];
    let totalSize = 0;

    // Reset ZIP instance
    this.zip = new JSZip();

    // Report initial progress
    this.reportProgress(onProgress, {
      stage: 'fetching',
      progress: 0,
      totalFiles: files.length,
      processedFiles: 0,
    });

    // Fetch and add each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const progress = Math.round((i / files.length) * 50); // Fetching is 0-50%

      this.reportProgress(onProgress, {
        stage: 'fetching',
        progress,
        currentFile: file.filename,
        totalFiles: files.length,
        processedFiles: i,
      });

      try {
        // Inline content (in-memory artifacts) is written directly; only
        // url-backed entries are fetched over the network.
        const blob =
          file.content != null ? new Blob([file.content]) : await this.fetchFile(file.url);
        totalSize += blob.size;

        // Determine the path inside ZIP
        const zipPath = file.folder ? `${file.folder}/${file.filename}` : file.filename;
        this.zip.file(zipPath, blob);
      } catch (error) {
        console.error(`Failed to fetch file ${file.filename}:`, error);
        failedFiles.push(file.filename);
      }
    }

    // Check if all files failed
    if (failedFiles.length === files.length) {
      this.reportProgress(onProgress, {
        stage: 'error',
        progress: 0,
        totalFiles: files.length,
        processedFiles: 0,
        error: 'All files failed to download',
      });
      return {
        success: false,
        error: 'All files failed to download',
        failedFiles,
      };
    }

    // Add manifest if requested
    if (includeManifest && manifestData) {
      const manifest = {
        ...manifestData,
        createdAt: new Date().toISOString(),
        files: files.map((f) => ({
          filename: f.filename,
          folder: f.folder,
          metadata: f.metadata,
        })),
        failedFiles,
      };
      this.zip.file('manifest.json', JSON.stringify(manifest, null, 2));
    }

    // Report compression progress
    this.reportProgress(onProgress, {
      stage: 'compressing',
      progress: 60,
      totalFiles: files.length,
      processedFiles: files.length - failedFiles.length,
    });

    // Generate the ZIP blob
    try {
      const blob = await this.zip.generateAsync(
        {
          type: 'blob',
          compression: 'DEFLATE',
          compressionOptions: { level: 6 },
        },
        (metadata) => {
          // Map compression progress to 60-95%
          const progress = 60 + Math.round(metadata.percent * 0.35);
          this.reportProgress(onProgress, {
            stage: 'compressing',
            progress,
            totalFiles: files.length,
            processedFiles: files.length - failedFiles.length,
          });
        }
      );

      // Create blob URL for download
      const blobUrl = URL.createObjectURL(blob);

      // Report completion
      this.reportProgress(onProgress, {
        stage: 'complete',
        progress: 100,
        totalFiles: files.length,
        processedFiles: files.length - failedFiles.length,
      });

      return {
        success: true,
        blobUrl,
        failedFiles: failedFiles.length > 0 ? failedFiles : undefined,
        totalSize,
      };
    } catch (error) {
      const errorMessage = handleError(
        error,
        'ZipDownloadService',
        'Unknown error during compression'
      );
      this.reportProgress(onProgress, {
        stage: 'error',
        progress: 0,
        totalFiles: files.length,
        processedFiles: files.length - failedFiles.length,
        error: errorMessage,
      });
      return {
        success: false,
        error: errorMessage,
        failedFiles,
      };
    }
  }

  /**
   * Download the ZIP archive to the user's device.
   *
   * @param blobUrl - Blob URL from createArchive result
   * @param filename - Desired filename (without .zip extension)
   */
  downloadArchive(blobUrl: string, filename: string): void {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename.endsWith('.zip') ? filename : `${filename}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Create and immediately download a ZIP archive.
   *
   * @param config - Archive configuration
   * @param onProgress - Optional progress callback
   * @returns Promise with the result
   */
  async createAndDownload(
    config: ZipArchiveConfig,
    onProgress?: ZipProgressCallback
  ): Promise<ZipResult> {
    const result = await this.createArchive(config, onProgress);

    if (result.success && result.blobUrl) {
      this.downloadArchive(result.blobUrl, config.archiveName);

      // Clean up blob URL after download starts
      setTimeout(() => {
        if (result.blobUrl) {
          URL.revokeObjectURL(result.blobUrl);
        }
      }, 1000);
    }

    return result;
  }

  /**
   * Fetch a file from URL and return as Blob.
   * Handles relative URLs by converting them to full URLs using URLService.
   */
  private async fetchFile(url: string): Promise<Blob> {
    // Convert relative URLs to full URLs (handles local dev vs production)
    const fullUrl = URLService.getFullUrl(url);
    const response = await fetch(fullUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response.blob();
  }

  /**
   * Report progress through callback if provided.
   */
  private reportProgress(callback: ZipProgressCallback | undefined, progress: ZipProgress): void {
    if (callback) {
      callback(progress);
    }
  }

  /**
   * Clean up a blob URL (call when done with archive).
   */
  static revokeBlobUrl(blobUrl: string): void {
    URL.revokeObjectURL(blobUrl);
  }
}

// Export singleton instance for convenience
export const zipDownloadService = new ZipDownloadService();

// Re-export types
export type { ZipArchiveConfig, ZipFileEntry, ZipProgress, ZipProgressCallback, ZipResult };
